import { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';

type PendingLeave =
  | { type: 'blocker' }
  | { type: 'navigate'; to: string }
  | { type: 'back' }
  | { type: 'callback'; run: () => void };

/**
 * Guard in-app navigation + tab close when the form is dirty.
 * Requires a data router (createBrowserRouter + RouterProvider).
 *
 * After the user confirms leave from our dialog (navigate/back/callback),
 * skip the next useBlocker cycle — otherwise navigate() re-opens the dialog.
 */
export const useUnsavedChangesGuard = (isDirty: boolean) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pending, setPending] = useState<PendingLeave | null>(null);
  const skipBlockerRef = useRef(false);

  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        if (skipBlockerRef.current) return false;
        if (!isDirty) return false;
        return (
          currentLocation.pathname !== nextLocation.pathname ||
          currentLocation.search !== nextLocation.search
        );
      },
      [isDirty]
    )
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setPending({ type: 'blocker' });
      setDialogOpen(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    if (!isDirty) {
      skipBlockerRef.current = false;
      return;
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const requestNavigate = useCallback(
    (to: string) => {
      if (!isDirty) {
        navigate(to);
        return;
      }
      setPending({ type: 'navigate', to });
      setDialogOpen(true);
    },
    [isDirty, navigate]
  );

  const requestBack = useCallback(() => {
    if (!isDirty) {
      navigate(-1);
      return;
    }
    setPending({ type: 'back' });
    setDialogOpen(true);
  }, [isDirty, navigate]);

  const requestLeave = useCallback(
    (run: () => void) => {
      if (!isDirty) {
        run();
        return;
      }
      setPending({ type: 'callback', run });
      setDialogOpen(true);
    },
    [isDirty]
  );

  const stay = useCallback(() => {
    skipBlockerRef.current = false;
    setDialogOpen(false);
    setPending(null);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const leave = useCallback(() => {
    setDialogOpen(false);
    const action = pending;
    setPending(null);

    // Confirmed leave — the navigation that follows must not open this dialog again.
    skipBlockerRef.current = true;

    if (action?.type === 'blocker' && blocker.state === 'blocked') {
      blocker.proceed();
      return;
    }

    if (action?.type === 'navigate') {
      navigate(action.to);
      return;
    }
    if (action?.type === 'back') {
      navigate(-1);
      return;
    }
    if (action?.type === 'callback') {
      action.run();
    }
  }, [pending, blocker, navigate]);

  /** Navigate after the caller already confirmed leave (skip the next blocker cycle). */
  const navigateWithoutGuard = useCallback(
    (to: string, options?: { state?: unknown }) => {
      skipBlockerRef.current = true;
      navigate(to, options);
    },
    [navigate]
  );

  /**
   * Caller already confirmed discard (overlay confirm). The next location change
   * must not be held by useBlocker — dialog presentation does not render this dialog.
   */
  const allowNextNavigation = useCallback(() => {
    skipBlockerRef.current = true;
    setDialogOpen(false);
    setPending(null);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  return {
    dialogOpen,
    requestNavigate,
    requestBack,
    requestLeave,
    navigateWithoutGuard,
    allowNextNavigation,
    stay,
    leave,
  };
};
